import React, { useEffect, useState } from "react";
import { fetchGamesHistory, fetchGame } from "../api";
import { SIDE_LABELS } from "../roles";
import PlayerBoard from "./PlayerBoard";
import ActionLog from "./ActionLog";
import GameReplay from "./GameReplay";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("uk-UA", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function winnerChipClass(side) {
  if (side === "mafia") return "role-chip-mafia";
  if (side === "town") return "role-chip-town";
  if (side === "draw") return "role-chip-draw";
  return "role-chip-solo";
}

// Нормалізує елемент списку з GET /games — бекенд може віддавати snake_case
// або camelCase, тож підстраховуємось під обидва варіанти.
function normalizeSummary(raw) {
  return {
    gameId: raw.gameId ?? raw.id,
    createdAt: raw.createdAt ?? raw.created_at ?? null,
    finishedAt: raw.finishedAt ?? raw.finished_at ?? null,
    playersCount: raw.playersCount ?? raw.players_count ?? raw.players?.length ?? null,
    roundsPlayed: raw.roundsPlayed ?? raw.rounds_played ?? raw.round ?? null,
    winnerSide: raw.winnerSide ?? raw.winner_side ?? null,
    status: raw.status ?? "finished",
  };
}

export default function GameHistoryScreen() {
  const [games, setGames] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [selectedGameId, setSelectedGameId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError("");
    fetchGamesHistory({ status: "finished" })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.games || [];
        setGames(list.map(normalizeSummary));
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setListError(e.message || "Не вдалося завантажити історію ігор.");
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function openGame(gameId) {
    setSelectedGameId(gameId);
    setDetail(null);
    setDetailError("");
    setDetailLoading(true);
    fetchGame(gameId)
      .then((res) => {
        setDetail({
          gameId: res.gameId ?? res.id ?? gameId,
          players: res.players || [],
          actions: res.actions || [],
          createdAt: res.createdAt ?? res.created_at ?? null,
          finishedAt: res.finishedAt ?? res.finished_at ?? null,
          winnerSide: res.winnerSide ?? res.winner_side ?? null,
        });
      })
      .catch((e) => {
        console.error(e);
        setDetailError(e.message || "Не вдалося завантажити цю гру.");
      })
      .finally(() => setDetailLoading(false));
  }

  function backToList() {
    setSelectedGameId(null);
    setDetail(null);
    setDetailError("");
  }

  if (selectedGameId) {
    return (
      <div className="screen">
        <button type="button" className="btn btn-ghost btn-small history-back" onClick={backToList}>
          ← До списку ігор
        </button>

        {detailLoading && <p className="hint-text">Завантажуємо гру…</p>}
        {detailError && !detailLoading && <p className="error-text">{detailError}</p>}

        {detail && !detailLoading && (
          <>
            <header className="setup-header">
              <p className="eyebrow-mark">Гра {detail.gameId}</p>
              <h1>Хронологія партії</h1>
              <p className="lede">
                {formatDate(detail.finishedAt || detail.createdAt)}
                {detail.winnerSide && (
                  <>
                    {" · перемога: "}
                    <span className={`role-chip ${winnerChipClass(detail.winnerSide)}`}>
                      {SIDE_LABELS[detail.winnerSide] || detail.winnerSide}
                    </span>
                  </>
                )}
              </p>
            </header>

            <h2>Хронологія</h2>
            <GameReplay players={detail.players} actions={detail.actions} />

            <h2>Гравці · фінальний стан</h2>
            <PlayerBoard players={detail.players} readOnly />

            <h2>Повний журнал подій</h2>
            <ActionLog players={detail.players} actions={detail.actions} readOnly />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="screen">
      <header className="setup-header">
        <p className="eyebrow-mark">Історія ігор</p>
        <h1>Хронологія зіграних партій</h1>
        <p className="lede">
          Оберіть гру зі списку, щоб переглянути повний журнал подій — хто кого вбив, кого
          вигнали, коли перевіряли.
        </p>
      </header>

      {listLoading && <p className="hint-text">Завантажуємо список ігор…</p>}
      {listError && !listLoading && <p className="error-text">{listError}</p>}

      {!listLoading && !listError && games.length === 0 && (
        <div className="empty-state">
          <p>Завершених ігор поки немає. Тут з'являться всі партії після завершення.</p>
        </div>
      )}

      {!listLoading && games.length > 0 && (
        <ul className="history-list">
          {games.map((g) => (
            <li key={g.gameId} className="history-card" onClick={() => openGame(g.gameId)}>
              <div className="history-card-main">
                <span className="history-card-title">Гра {g.gameId}</span>
                <span className="history-card-meta">
                  {formatDate(g.finishedAt || g.createdAt)}
                  {g.playersCount != null && ` · ${g.playersCount} гравців`}
                  {g.roundsPlayed != null && ` · ${g.roundsPlayed} раундів`}
                </span>
              </div>
              {g.winnerSide && (
                <span className={`role-chip ${winnerChipClass(g.winnerSide)}`}>
                  {SIDE_LABELS[g.winnerSide] || g.winnerSide}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
